import { CANVAS_ELEMENT_TYPES } from "../nativeCanvasElements";
import { getArrowHeadPoints } from "./arrowGeometry";

const DEFAULT_ARROW_LENGTH = 120;
const DEFAULT_BOX_SIZE = 120;
const DEFAULT_IMAGE_SIZE = 160;
const DEFAULT_TEXT_FONT_SIZE = 22;
const MIN_BOUND_SIZE = 1;
const TEXT_LINE_HEIGHT_FACTOR = 1.3;
const TEXT_WIDTH_FACTOR = 0.6;

const getNumber = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const getTextLines = (element) =>
  String(element.text ?? element.content ?? "").split("\n");

const getTextFontSize = (element) =>
  getNumber(element.style?.fontSize ?? element.fontSize, DEFAULT_TEXT_FONT_SIZE);

/**
 * @param {object} element
 * @param {number} [fallbackSize]
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const getElementFrame = (element, fallbackSize = DEFAULT_BOX_SIZE) => ({
  height: Math.max(
    getNumber(element.height ?? element.h, fallbackSize),
    MIN_BOUND_SIZE
  ),
  width: Math.max(
    getNumber(element.width ?? element.w, fallbackSize),
    MIN_BOUND_SIZE
  ),
  x: getNumber(element.x, 0),
  y: getNumber(element.y, 0),
});

/**
 * @param {object} element
 * @returns {{ x1: number, y1: number, x2: number, y2: number }}
 */
export const getArrowPoints = (element) => {
  const x1 = getNumber(element.x1 ?? element.startX ?? element.x, 0);
  const y1 = getNumber(element.y1 ?? element.startY ?? element.y, 0);

  return {
    x1,
    x2: getNumber(element.x2 ?? element.endX, x1 + DEFAULT_ARROW_LENGTH),
    y1,
    y2: getNumber(element.y2 ?? element.endY, y1),
  };
};

const getArrowBounds = (element) => {
  const points = getArrowPoints(element);
  const { leftWing, rightWing, tip } = getArrowHeadPoints(points);
  const xValues = [points.x1, leftWing.x, rightWing.x, tip.x];
  const yValues = [points.y1, leftWing.y, rightWing.y, tip.y];
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);

  return {
    height: Math.max(Math.max(...yValues) - minY, MIN_BOUND_SIZE),
    width: Math.max(Math.max(...xValues) - minX, MIN_BOUND_SIZE),
    x: minX,
    y: minY,
  };
};

const getPathBounds = (element) => {
  const points = Array.isArray(element.points) ? element.points : [];
  if (!points.length) return getElementFrame(element);

  const xValues = points.map((point) => getNumber(point.x, 0));
  const yValues = points.map((point) => getNumber(point.y, 0));
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);

  return {
    height: Math.max(Math.max(...yValues) - minY, MIN_BOUND_SIZE),
    width: Math.max(Math.max(...xValues) - minX, MIN_BOUND_SIZE),
    x: minX,
    y: minY,
  };
};

const getTextBounds = (element) => {
  const fontSize = getTextFontSize(element);
  const lines = getTextLines(element);
  const longestLineLength = Math.max(...lines.map((line) => line.length), 1);

  return {
    height: Math.max(
      lines.length * fontSize * TEXT_LINE_HEIGHT_FACTOR,
      MIN_BOUND_SIZE
    ),
    width: Math.max(
      longestLineLength * fontSize * TEXT_WIDTH_FACTOR,
      fontSize
    ),
    x: getNumber(element.x, 0),
    y: getNumber(element.y, 0),
  };
};

/**
 * Axis-aligned bounding box of any canvas element in scene coordinates.
 * @param {{ type?: string }} element
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const getElementBounds = (element) => {
  if (element.type === CANVAS_ELEMENT_TYPES.ARROW) return getArrowBounds(element);
  if (element.type === CANVAS_ELEMENT_TYPES.PATH) return getPathBounds(element);
  if (element.type === CANVAS_ELEMENT_TYPES.TEXT) return getTextBounds(element);
  if (element.type === CANVAS_ELEMENT_TYPES.IMAGE) {
    return getElementFrame(element, DEFAULT_IMAGE_SIZE);
  }

  return getElementFrame(element);
};

/**
 * @param {Array<object>} elements
 * @returns {{ x: number, y: number, width: number, height: number } | null}
 */
export const getSelectionBounds = (elements) => {
  if (!elements.length) return null;

  const boundsList = elements.map(getElementBounds);
  const xValues = boundsList.flatMap((bounds) => [bounds.x, bounds.x + bounds.width]);
  const yValues = boundsList.flatMap((bounds) => [bounds.y, bounds.y + bounds.height]);
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);

  return {
    height: Math.max(...yValues) - minY,
    width: Math.max(...xValues) - minX,
    x: minX,
    y: minY,
  };
};

/**
 * @param {{ x: number, y: number, width: number, height: number }} firstBox
 * @param {{ x: number, y: number, width: number, height: number }} secondBox
 * @returns {boolean}
 */
export const doBoxesIntersect = (firstBox, secondBox) =>
  firstBox.x <= secondBox.x + secondBox.width &&
  firstBox.x + firstBox.width >= secondBox.x &&
  firstBox.y <= secondBox.y + secondBox.height &&
  firstBox.y + firstBox.height >= secondBox.y;

/**
 * @param {{ box: { x: number, y: number, width: number, height: number }, padding?: number, point: { x: number, y: number } }} params
 * @returns {boolean}
 */
export const isPointInBox = ({ box, padding = 0, point }) =>
  point.x >= box.x - padding &&
  point.x <= box.x + box.width + padding &&
  point.y >= box.y - padding &&
  point.y <= box.y + box.height + padding;
